import React, {useEffect, useRef, useState} from 'react';
import {PRISMAX_ADMIN_DOWNLOADS_URL, PRISMAX_BACKEND_URL} from '../../configs/config';
import {getApiErrorMessage, isSessionExpiredError} from '../../utils/authErrors';
import styles from './AdminBrowserDownloads.module.css';

export default function AdminDownloadVerificationDialog({adminToken, episodeIds, onClose, onReady, onSessionExpired}) {
    const [recipient, setRecipient] = useState('jason');
    const [challenge, setChallenge] = useState(null);
    const [code, setCode] = useState('');
    const [pending, setPending] = useState(false);
    const [error, setError] = useState('');
    const [resendAt, setResendAt] = useState(0);
    const [now, setNow] = useState(Date.now());
    const dialogRef = useRef(null);
    const codeRef = useRef(null);
    const requestRef = useRef(null);
    const pendingRef = useRef(false);
    const remaining = Math.max(0, Math.ceil((resendAt - now) / 1000));
    const expired = challenge && now >= Date.parse(challenge.expires_at);

    useEffect(() => {
        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialogRef.current?.querySelector('input')?.focus();
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => {
            clearInterval(timer);
            requestRef.current?.abort();
            document.body.style.overflow = previousOverflow;
            previousFocus?.focus();
        };
    }, []);

    useEffect(() => {
        if (challenge && !pending) codeRef.current?.focus();
    }, [challenge, pending]);

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            if (!pendingRef.current) onClose();
        }
        if (event.key === 'Tab') {
            const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled)')];
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
            if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
    };

    const submit = async (verify) => {
        if (pendingRef.current || (!verify && remaining) || (verify && (!challenge || expired || !/^[0-9]{8}$/.test(code)))) return;
        pendingRef.current = true;
        setPending(true);
        setError('');
        const controller = new AbortController();
        requestRef.current = controller;
        if (!verify) {
            setChallenge(null);
            setCode('');
            setResendAt(Date.now() + 60000);
        }
        try {
            const response = await fetch(`${verify ? PRISMAX_ADMIN_DOWNLOADS_URL : PRISMAX_BACKEND_URL}/data/admin/downloads/${verify ? 'ui' : 'verification'}`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}`},
                body: JSON.stringify(verify ? {
                    selected_episode_ids: episodeIds, challenge_id: challenge.challenge_id, verification_code: code,
                } : {selected_episode_ids: episodeIds, recipient}),
                signal: controller.signal,
            });
            const json = await response.json();
            if (!response.ok || !json.success) throw new Error(getApiErrorMessage(json, 'Verification failed. Please try again.'));
            if (controller.signal.aborted) return;
            if (verify) {
                onReady(json.data);
            } else {
                setChallenge(json.data);
                setResendAt(Date.now() + json.data.resend_after_seconds * 1000);
            }
        } catch (err) {
            if (!controller.signal.aborted) {
                if (isSessionExpiredError(err.message)) onSessionExpired?.();
                setError(err.message || 'Verification failed. Please request a new code.');
            }
        } finally {
            pendingRef.current = false;
            if (!controller.signal.aborted) setPending(false);
        }
    };

    return <div className={styles.modalBackdrop}>
        <div ref={dialogRef} className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="admin-download-verification-title" onKeyDown={handleKeyDown}>
            <h2 id="admin-download-verification-title">Verify your download</h2>
            <p>Send a code to a recipient, then enter it to download these {episodeIds.length} episodes.</p>
            <fieldset disabled={pending} className={styles.recipients}>
                <legend>Send verification code to</legend>
                {[['jason', 'Ja****'], ['bayley', 'Ba***'], ['chris', 'Ch***']].map(([value, label]) => <label key={value}>
                    <input type="radio" name="download-code-recipient" value={value} checked={recipient === value} onChange={() => {
                        setRecipient(value); setChallenge(null); setCode(''); setError('');
                    }}/>{label}
                </label>)}
            </fieldset>
            <button type="button" disabled={pending || remaining > 0} onClick={() => void submit(false)}>
                {remaining > 0 ? `Resend in ${remaining}s` : challenge ? 'Resend code' : 'Send code'}
            </button>
            {challenge && <p role="status">Code sent to {challenge.recipient_label}. Valid for 5 minutes.</p>}
            {expired && <p className={styles.error}>Code expired. Please request a new code.</p>}
            <form onSubmit={(event) => { event.preventDefault(); void submit(true); }}>
                <label className={styles.codeLabel} htmlFor="admin-download-code">8-digit verification code</label>
                <input ref={codeRef} id="admin-download-code" className={styles.codeInput} type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={8}
                    value={code} disabled={!challenge || pending || expired} onChange={(event) => setCode(event.target.value.replace(/[^0-9]/g, '').slice(0, 8))}/>
                <p className={styles.note}>The code works once for this selection. Requesting a new code invalidates the previous one.</p>
                {error && <p role="alert" className={styles.error}>{error}</p>}
                <div className={styles.toolbar}>
                    <button type="button" disabled={pending} onClick={onClose}>Cancel</button>
                    <button type="submit" className={styles.primary} disabled={pending || !challenge || expired || !/^[0-9]{8}$/.test(code)}>
                        {pending ? 'Please wait…' : 'Verify and download'}
                    </button>
                </div>
            </form>
        </div>
    </div>;
}
